import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PartyPopper, X, Trophy } from "lucide-react";
import confetti from "canvas-confetti";

/**
 * One-time celebration modal that fires on the FIRST paid order.
 *
 * Renders nothing until the poll returns should_celebrate=true. Then:
 *   - shoots two rounds of canvas-confetti (side-cannons style)
 *   - displays a modal with the order total + a "Nice one" ack button
 *   - POSTs the ack so it never fires again on this surface
 *
 * Props:
 *   api        — an admin axios instance (buildAdminApi(slug))
 *   surface    — "admin" or "staff" (drives which endpoint we hit + the copy)
 *   cafeName   — for the greeting
 */
const FirstPaymentCelebration = ({ api, surface = "admin", cafeName }) => {
    const [payload, setPayload] = useState(null); // {should_celebrate, order}
    const [open, setOpen] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    // Guard so we never double-fire the confetti even if React strict-mode
    // remounts. Also protects against race in the two useEffects below.
    const firedRef = useRef(false);

    const path = surface === "staff" ? "/staff" : "/admin";

    // Poll once on mount. That's enough — the moment is one-time.
    useEffect(() => {
        let alive = true;
        api.get(`${path}/first-payment/status`)
            .then((r) => {
                if (!alive) return;
                setPayload(r.data);
                if (r.data?.should_celebrate) {
                    setOpen(true);
                }
            })
            .catch(() => {
                // Silent — surface would just NOT celebrate if endpoint 404s.
            });
        return () => {
            alive = false;
        };
    }, [api, path]);

    // Confetti effect ties to `open` — runs the burst as the modal appears.
    useEffect(() => {
        if (!open || firedRef.current) return;
        firedRef.current = true;
        // Side-cannon confetti, ~2s of celebration. Uses two shots per side
        // for a beefier reveal without lag.
        const duration = 1800;
        const end = Date.now() + duration;
        const colors = ["#FF4F18", "#FFD166", "#0E6B4C", "#2C1A12", "#FFFFFF"];
        (function frame() {
            confetti({
                particleCount: 4,
                angle: 60,
                spread: 65,
                origin: { x: 0, y: 0.7 },
                colors,
                startVelocity: 55,
                scalar: 0.9,
            });
            confetti({
                particleCount: 4,
                angle: 120,
                spread: 65,
                origin: { x: 1, y: 0.7 },
                colors,
                startVelocity: 55,
                scalar: 0.9,
            });
            if (Date.now() < end) requestAnimationFrame(frame);
        })();
        // A single wide burst up top for the reveal moment.
        setTimeout(() => {
            confetti({
                particleCount: 90,
                spread: 100,
                origin: { y: 0.35 },
                colors,
                startVelocity: 45,
            });
        }, 200);
    }, [open]);

    const dismiss = async () => {
        if (dismissing) return;
        setDismissing(true);
        // Ack first (fire-and-forget on error — the modal still closes).
        try {
            await api.post(`${path}/first-payment/ack`);
        } catch (_) {
            /* swallow — worst case they see the modal one more time */
        }
        setOpen(false);
    };

    if (!payload?.should_celebrate) return null;

    const order = payload.order || {};
    const total = Number(order.total || 0);
    const headline =
        surface === "staff"
            ? "First order at the counter!"
            : "You just made your first sale!";
    const subline =
        surface === "staff"
            ? "Serve it with a smile — this one's for the memoir."
            : `${cafeName || "Your café"} is officially open for business.`;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="first-pay-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] bg-[#2C1A12]/70 backdrop-blur-sm flex items-center justify-center p-6"
                    data-testid="first-payment-modal"
                >
                    <motion.div
                        initial={{ scale: 0.7, y: 30, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 22 }}
                        className="relative w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-[0_40px_80px_rgba(44,26,18,0.4)] overflow-hidden"
                    >
                        {/* Decorative peel corner */}
                        <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-[#FF4F18]/15" />
                        <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-[#FFD166]/20" />

                        <button
                            onClick={dismiss}
                            data-testid="first-payment-dismiss"
                            aria-label="Close celebration"
                            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#F9F6F0] hover:bg-[#E8DCCB] flex items-center justify-center transition-colors"
                        >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>

                        <div className="relative">
                            <motion.div
                                initial={{ rotate: -20, scale: 0 }}
                                animate={{ rotate: 0, scale: 1 }}
                                transition={{ type: "spring", stiffness: 200, delay: 0.15 }}
                                className="mx-auto inline-flex h-20 w-20 rounded-full bg-gradient-to-br from-[#FF4F18] to-[#FFD166] items-center justify-center text-white shadow-lg"
                            >
                                <Trophy className="h-10 w-10" strokeWidth={2.2} />
                            </motion.div>

                            <div className="mt-5 text-xs uppercase tracking-[0.3em] font-black text-[#FF4F18]">
                                <PartyPopper className="inline h-3.5 w-3.5 mr-1 -mt-0.5" strokeWidth={2.5} />
                                First paid order · Coffeein7
                            </div>

                            <h2 className="mt-3 font-display font-black text-3xl leading-tight text-[#2C1A12]">
                                {headline}
                            </h2>
                            <p className="mt-2 text-sm text-[#8C7A72]">{subline}</p>

                            {total > 0 && (
                                <motion.div
                                    initial={{ scale: 0.85, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.35 }}
                                    className="mt-6 rounded-2xl bg-[#F9F6F0] border-2 border-dashed border-[#E8DCCB] p-5"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                        Order #{order.order_number || "—"}
                                    </div>
                                    <div className="mt-1 font-display font-black text-4xl text-[#2C1A12]">
                                        ${total.toFixed(2)}
                                    </div>
                                </motion.div>
                            )}

                            <button
                                onClick={dismiss}
                                disabled={dismissing}
                                data-testid="first-payment-ack"
                                className="mt-7 w-full py-4 rounded-full bg-[#2C1A12] text-white font-display font-bold text-base active:scale-[0.98] shadow-[0_12px_32px_rgba(44,26,18,0.32)] disabled:opacity-60"
                            >
                                {dismissing ? "Saving…" : surface === "staff" ? "Let's make it perfect →" : "Onwards ☕️"}
                            </button>

                            <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#8C7A72] font-semibold">
                                This moment only happens once
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default FirstPaymentCelebration;
